/** Projects and their usage limits. */
import { z } from 'zod';
import { slug, uuid } from './common.js';
import { Environment } from './environments.js';
import { BranchName } from './git.js';

export const ProjectCreateRequest = z.object({
  slug,
  name: z.string().min(1).max(120),
  description: z.string().max(2000).optional(),
  createDefaultEnvironments: z.boolean().default(true),
  /**
   * The branch the protected default environments accept: preprod gets it and `release/*`, production only it
   * (default `master`). Only with `createDefaultEnvironments`; every environment stays editable afterwards.
   */
  productionBranch: BranchName.optional(),
}).refine((p) => p.productionBranch === undefined || p.createDefaultEnvironments, {
  path: ['productionBranch'], message: 'productionBranch shapes the default environments: it needs createDefaultEnvironments',
});
export type ProjectCreateRequest = z.infer<typeof ProjectCreateRequest>;

/** Upper bounds of the per-project usage limits a patch may set. */
export const PROJECT_USAGE_LIMITS_MAX = { maxConcurrentBuilds: 100, maxBuildsPerDay: 10_000, maxQueuedBuilds: 1_000 } as const;

/**
 * `ProjectPatch.usageLimits` (merge patch): a number sets the limit, `null` removes it, an absent field is unchanged.
 * Only `maxConcurrentBuilds` is enforced today (the scheduler, SPEC §8.3); `maxBuildsPerDay` and `maxQueuedBuilds`
 * are stored for the usage limits of SPEC §18 and not enforced yet.
 */
export const ProjectUsageLimitsPatch = z.object({
  maxConcurrentBuilds: z.number().int().min(1).max(PROJECT_USAGE_LIMITS_MAX.maxConcurrentBuilds).nullable().optional(),
  maxBuildsPerDay: z.number().int().min(1).max(PROJECT_USAGE_LIMITS_MAX.maxBuildsPerDay).nullable().optional(),
  maxQueuedBuilds: z.number().int().min(1).max(PROJECT_USAGE_LIMITS_MAX.maxQueuedBuilds).nullable().optional(),
}).strict();
export type ProjectUsageLimitsPatch = z.infer<typeof ProjectUsageLimitsPatch>;

export const ProjectPatch = z.object({
  name: z.string().min(1).max(120).optional(),
  description: z.string().max(2000).nullable().optional(),
  archived: z.boolean().optional(),
  /** Needs `admin` or the project's `maintainer` role, like environments. */
  usageLimits: ProjectUsageLimitsPatch.optional(),
}).strict();
export type ProjectPatch = z.infer<typeof ProjectPatch>;

/** `Project.usageLimits`: the project's own values, `null` = not set (no project limit). */
export const ProjectUsageLimits = z.object({
  maxConcurrentBuilds: z.number().int().nullable(),
  maxBuildsPerDay: z.number().int().nullable(),
  maxQueuedBuilds: z.number().int().nullable(),
});
export type ProjectUsageLimits = z.infer<typeof ProjectUsageLimits>;

export const Project = z.object({
  id: uuid, orgId: uuid, slug, name: z.string(), description: z.string().nullable(),
  environments: z.array(Environment), createdAt: z.string(), archivedAt: z.string().nullable(), etag: z.string(),
  /** Absent from an API that predates it (and from the console's mock). */
  usageLimits: ProjectUsageLimits.optional(),
});
export type Project = z.infer<typeof Project>;
